import { create } from "zustand";
import { persist } from "zustand/middleware";

export type TerminalTabState = {
	id: string;
	title: string;
	status: "connecting" | "ready" | "exited";
	customTitle?: string;
};

interface DesktopTerminalState {
	tabs: TerminalTabState[];
	hiddenCardTabs: TerminalTabState[];
	activeBackendSessionIds: string[];
	activeTabId: string;
	openTab: (id: string, title?: string) => void;
	createTab: (title?: string) => string;
	hideTab: (id: string) => void;
	terminateTab: (id: string) => void;
	closeTab: (id: string) => void;
	setActiveTabId: (id: string) => void;
	setTabStatus: (id: string, status: "connecting" | "ready" | "exited") => void;
	setTabTitle: (id: string, title: string) => void;
	renameTab: (id: string, newTitle: string) => void;
	setActiveBackendSessionIds: (ids: string[]) => void;
}

const DEFAULT_MAIN_TAB_ID = "term_main";

const DEFAULT_MAIN_TAB: TerminalTabState = {
	id: DEFAULT_MAIN_TAB_ID,
	title: "Terminal 1",
	status: "connecting",
};

function generateTabId(): string {
	return `term_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
}

function pickNextActiveTabId(
	tabs: TerminalTabState[],
	idToRemove: string,
	activeTabId: string,
): string {
	const remainingTabs = tabs.filter((t) => t.id !== idToRemove);
	if (activeTabId !== idToRemove) {
		return activeTabId;
	}
	const closedIndex = tabs.findIndex((t) => t.id === idToRemove);
	const nextTab =
		remainingTabs[Math.max(0, closedIndex - 1)] || remainingTabs[0];
	return nextTab?.id ?? DEFAULT_MAIN_TAB_ID;
}

export const useDesktopTerminalStore = create<DesktopTerminalState>()(
	persist(
		(set, get) => ({
			tabs: [{ ...DEFAULT_MAIN_TAB }],
			hiddenCardTabs: [],
			activeBackendSessionIds: [],
			activeTabId: DEFAULT_MAIN_TAB_ID,

			openTab: (id: string, title?: string) => {
				const { tabs, hiddenCardTabs } = get();

				const hiddenTab = hiddenCardTabs.find((t) => t.id === id);
				const nextHidden = hiddenCardTabs.filter((t) => t.id !== id);

				const existingIndex = tabs.findIndex((t) => t.id === id);
				if (existingIndex >= 0) {
					set({
						hiddenCardTabs: nextHidden,
						activeTabId: id,
						tabs: tabs.map((t) => {
							if (t.id !== id) return t;
							const resolvedTitle = t.customTitle || title || t.title;
							return { ...t, title: resolvedTitle };
						}),
					});
					return;
				}

				const resolvedTitle =
					hiddenTab?.customTitle ||
					hiddenTab?.title ||
					title ||
					`Terminal ${tabs.length + 1}`;

				const newTab: TerminalTabState = {
					id,
					title: resolvedTitle,
					customTitle: hiddenTab?.customTitle,
					status: hiddenTab?.status || "connecting",
				};

				set({
					hiddenCardTabs: nextHidden,
					tabs: [...tabs, newTab],
					activeTabId: id,
				});
			},

			createTab: (title?: string) => {
				const newId = generateTabId();
				const { tabs } = get();
				const tabIndex = tabs.length + 1;
				const cleanTitle = title?.trim() || undefined;
				const newTab: TerminalTabState = {
					id: newId,
					title: cleanTitle || `Terminal ${tabIndex}`,
					customTitle: cleanTitle,
					status: "connecting",
				};

				set({
					tabs: [...tabs, newTab],
					activeTabId: newId,
				});

				return newId;
			},

			hideTab: (idToHide: string) => {
				const { tabs, hiddenCardTabs, activeTabId } = get();
				const tabToHide = tabs.find((t) => t.id === idToHide);

				let nextHidden = hiddenCardTabs;
				if (tabToHide && idToHide.startsWith("card_")) {
					const alreadyHidden = hiddenCardTabs.some((t) => t.id === idToHide);
					if (!alreadyHidden) {
						nextHidden = [...hiddenCardTabs, tabToHide];
					} else {
						nextHidden = hiddenCardTabs.map((t) =>
							t.id === idToHide ? { ...t, ...tabToHide } : t,
						);
					}
				}

				if (tabs.length <= 1) {
					const mainTab = tabs.find((t) => t.id === DEFAULT_MAIN_TAB_ID);
					const mainTitle =
						mainTab?.customTitle || mainTab?.title || DEFAULT_MAIN_TAB.title;
					set({
						hiddenCardTabs: nextHidden,
						tabs: [
							{
								...DEFAULT_MAIN_TAB,
								title: mainTitle,
								customTitle: mainTab?.customTitle,
							},
						],
						activeTabId: DEFAULT_MAIN_TAB_ID,
					});
					return;
				}

				set({
					hiddenCardTabs: nextHidden,
					tabs: tabs.filter((t) => t.id !== idToHide),
					activeTabId: pickNextActiveTabId(tabs, idToHide, activeTabId),
				});
			},

			terminateTab: (idToTerminate: string) => {
				const { tabs, hiddenCardTabs, activeTabId } = get();
				const nextHidden = hiddenCardTabs.filter((t) => t.id !== idToTerminate);

				if (tabs.length <= 1) {
					const mainTab = tabs.find((t) => t.id === DEFAULT_MAIN_TAB_ID);
					const mainTitle =
						mainTab?.customTitle || mainTab?.title || DEFAULT_MAIN_TAB.title;
					set({
						hiddenCardTabs: nextHidden,
						tabs: [
							{
								...DEFAULT_MAIN_TAB,
								title: mainTitle,
								customTitle: mainTab?.customTitle,
							},
						],
						activeTabId: DEFAULT_MAIN_TAB_ID,
					});
					return;
				}

				set({
					hiddenCardTabs: nextHidden,
					tabs: tabs.filter((t) => t.id !== idToTerminate),
					activeTabId: pickNextActiveTabId(tabs, idToTerminate, activeTabId),
				});
			},

			closeTab: (idToClose: string) => {
				if (idToClose.startsWith("card_")) {
					get().hideTab(idToClose);
				} else {
					get().terminateTab(idToClose);
				}
			},

			setActiveTabId: (id: string) => {
				set({ activeTabId: id });
			},

			setTabStatus: (id: string, status: "connecting" | "ready" | "exited") => {
				set((state) => ({
					tabs: state.tabs.map((t) => (t.id === id ? { ...t, status } : t)),
					hiddenCardTabs: state.hiddenCardTabs.map((t) =>
						t.id === id ? { ...t, status } : t,
					),
				}));
			},

			setTabTitle: (id: string, title: string) => {
				set((state) => ({
					tabs: state.tabs.map((t) => (t.id === id ? { ...t, title } : t)),
					hiddenCardTabs: state.hiddenCardTabs.map((t) =>
						t.id === id ? { ...t, title } : t,
					),
				}));
			},

			renameTab: (id: string, newTitle: string) => {
				const cleanTitle = newTitle.trim();
				if (!cleanTitle) return;
				set((state) => ({
					tabs: state.tabs.map((t) =>
						t.id === id
							? { ...t, title: cleanTitle, customTitle: cleanTitle }
							: t,
					),
					hiddenCardTabs: state.hiddenCardTabs.map((t) =>
						t.id === id
							? { ...t, title: cleanTitle, customTitle: cleanTitle }
							: t,
					),
				}));
			},

			setActiveBackendSessionIds: (ids: string[]) => {
				set({ activeBackendSessionIds: ids });
			},
		}),
		{
			name: "illumi-desktop-terminal-tabs",
			partialize: (state) => ({
				tabs: state.tabs.map((t) => ({
					id: t.id,
					title: t.title,
					customTitle: t.customTitle,
					status: "connecting" as const,
				})),
				hiddenCardTabs: state.hiddenCardTabs.map((t) => ({
					id: t.id,
					title: t.title,
					customTitle: t.customTitle,
					status: "connecting" as const,
				})),
				activeTabId: state.activeTabId,
			}),
		},
	),
);
